import { theme } from 'lib/theme';
import React from 'react';
import {
  LayoutChangeEvent,
  Platform,
  StyleProp,
  StyleSheet,
  View,
  ViewProps,
  ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BlurView } from 'expo-blur';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { List, ListProps, ListRef } from './List/List';
import { NAVIGATION_HEADER_HEIGHT } from './NavigationHeader';
import { PageEmpty, PageEmptyProps } from './PageEmpty';
import { ActivityIndicator } from './BlockingLoader/ActivityIndicator';
import { ScrollView } from './ScrollView';

const HEADER_BLUR_INTENSITY = 50;
const SHOW_FLOATING_HEADER = Platform.OS === 'ios';

type Props = {
  header?: React.ReactNode;
  backgroundColor?: string;
  pageStyle?: StyleProp<ViewStyle>;
  isInModal?: boolean;
};

type PropsPage = Props & {
  contentPaddingTop?: number;
  contentPaddingBottom?: number;
  children?: React.ReactNode | React.ReactNode[];
  ignoreSafeAreaBottom?: boolean;
  showFloatingHeader?: boolean;
};

type PropsScrollView = PropsPage & {
  scrollEnabled?: boolean;
  contentContainerStyle?: StyleProp<ViewStyle>;
  emptyChild?: React.ReactNode;
  onLayout?: ViewProps['onLayout'];
};

export type PageFlatListRef<T> = {
  // scrollToIndex: FlashList<T>['scrollToIndex'];
  scrollToItem: ListRef<T>['scrollToItem'];
};

type PropsFlatList<ItemT> = Props & {
  listConfig: Omit<ListProps<ItemT>, 'contentContainerStyle'>;
  contentPaddingBottom?: number;
  contentPaddingBottomAdditional?: number;
  contentPaddingLeft?: number;
  contentPaddingRight?: number;
  contentPaddingTop?: number;
  emptyConfig?: PageEmptyProps;
  emptyChild?: React.ReactNode;
  isLoading?: boolean;
  underHeaderAbsolute?: React.ReactNode;
  pageRef?: React.ForwardedRef<PageFlatListRef<ItemT>> | null;
  /** Only returns height changes when showFloatingHeader=true */
  onHeaderHeightChange?: (height: number) => void;
};

export const PageFlatList = <ListItemT,>({
  pageStyle,
  backgroundColor,
  contentPaddingBottom = theme.paddingPageVertical + theme.paddingVertical * 2,
  contentPaddingTop = 0,
  header,
  underHeaderAbsolute: underHeader,
  listConfig,
  contentPaddingBottomAdditional = 0,
  contentPaddingLeft = 0,
  contentPaddingRight = 0,
  emptyConfig,
  emptyChild,
  isLoading,
  isInModal = true,
  pageRef,
  onHeaderHeightChange,
}: PropsFlatList<ListItemT>): React.ReactElement => {
  const listRef = React.useRef<ListRef<ListItemT>>(null);

  React.useImperativeHandle(
    pageRef,
    () => ({
      // scrollToIndex: (...args) => flastListRef.current!.scrollToIndex(...args),
      scrollToItem: (...args) => listRef.current!.scrollToItem(...args),
    }),
    [listRef],
  );

  const {
    left,
    right,
    top: safeAreaTop,
    bottom: safeAreaBottom,
  } = useSafeAreaInsets();

  const top = !isInModal ? safeAreaTop : 0;
  const bottom = !isInModal
    ? safeAreaBottom
    : Platform.OS === 'ios' && !Platform.isPad
      ? safeAreaBottom
      : 0;

  const showFloatingHeader = listConfig.refreshControl // || listConfig.stickyHeaderIndices?.length
    ? false
    : SHOW_FLOATING_HEADER;
  const [headerHeight, setHeaderHeight] = React.useState(
    showFloatingHeader ? NAVIGATION_HEADER_HEIGHT + top : 0,
  );
  const handleHeaderLayout = React.useCallback(
    ({
      nativeEvent: {
        layout: { height },
      },
    }: LayoutChangeEvent) => {
      const roundedHeight = Math.round(height);
      setHeaderHeight(roundedHeight);
      onHeaderHeightChange?.(roundedHeight);
    },
    [onHeaderHeightChange],
  );

  const hasData = !!listConfig.data?.length;
  const paddingTop = headerHeight + contentPaddingTop;
  const marginBottom =
    Platform.OS === 'ios' && listConfig.data?.length
      ? headerHeight + contentPaddingTop
      : 0;
  const paddingBottom =
    bottom +
    contentPaddingBottom +
    contentPaddingBottomAdditional +
    marginBottom;
  const paddingLeft = left + contentPaddingLeft;
  const paddingRight = right + contentPaddingRight;

  // React.useEffect(() => {
  //   setTimeout(() => {
  //     if (listRef.current) {
  //       console.log('ScrollToIndex');
  //       listRef.current.scrollToIndex({ index: 4, animated: true });
  //       // console.log(listRef.current);
  //     }
  //   }, 1000);
  // }, [listRef]);

  return (
    <View
      style={[
        {
          flex: 1,
          backgroundColor: backgroundColor || theme.colors.background,
        },
        pageStyle,
      ]}
    >
      {!showFloatingHeader && (
        <>
          <View style={[styles.headerNotFloating, { paddingTop: top }]}>
            {header}
          </View>
          {underHeader && (
            <View
              style={{
                position: 'relative',
                zIndex: 1,
              }}
            >
              <View
                style={{
                  position: 'absolute',
                  zIndex: 1,
                  top: headerHeight,
                  left: 0,
                  right: 0,
                }}
              >
                {underHeader}
              </View>
            </View>
          )}
        </>
      )}
      <View style={{ flex: 1, overflow: 'hidden' }}>
        {isLoading && (
          <View
            accessibilityRole="progressbar"
            accessibilityLabel="Please Wait"
            style={[
              StyleSheet.absoluteFill,
              {
                alignItems: 'center',
                backgroundColor: theme.colors.white50,
                justifyContent: 'center',
                zIndex: 2,
              },
            ]}
          >
            <ActivityIndicator size={60} />
          </View>
        )}
        {!hasData && !!emptyChild && (
          <View
            style={{
              position: 'absolute',
              top: headerHeight,
              left: 0,
              bottom: 0,
              right: 0,
              zIndex: 1,
            }}
          >
            {emptyChild}
          </View>
        )}
        {!hasData && !!emptyConfig && (
          <PageEmpty
            text={emptyConfig.text}
            subtext={emptyConfig.subtext}
            subtextHeight={emptyConfig.subtextHeight}
            icon={emptyConfig.icon}
            button={emptyConfig.button}
            style={{
              position: 'absolute',
              top: headerHeight,
              left: 0,
              bottom: 0,
              right: 0,
              zIndex: 1,
            }}
          />
        )}
        <List<ListItemT>
          listRef={listRef}
          threshold={listConfig.threshold}
          onEndReached={listConfig.onEndReached}
          renderItem={listConfig.renderItem}
          getItemType={listConfig.getItemType}
          refreshControl={listConfig.refreshControl}
          data={listConfig.data}
          extraData={listConfig.extraData}
          ListFooterComponent={listConfig.ListFooterComponent}
          ListHeaderComponent={listConfig.ListHeaderComponent}
          ListHeaderComponentStyle={listConfig.ListHeaderComponentStyle}
          numberOfColumns={listConfig.numberOfColumns}
          keyExtractor={listConfig.keyExtractor}
          ItemSeparatorComponent={listConfig.ItemSeparatorComponent}
          stickyHeaderIndices={listConfig.stickyHeaderIndices}
          maintainVisibleContentPosition={
            listConfig.maintainVisibleContentPosition
          }
          onScroll={listConfig.onScroll}
          onLayout={listConfig.onLayout}
          scrollIndicatorInsets={
            headerHeight ? { top: headerHeight - top } : {}
          }
          contentContainerStyle={{
            paddingTop,
            paddingBottom,
            paddingLeft,
            paddingRight,
          }}
        />
      </View>
      {showFloatingHeader && (
        <>
          <BlurView
            onLayout={handleHeaderLayout}
            intensity={HEADER_BLUR_INTENSITY}
            tint="light"
            style={[styles.headerFloating, { paddingTop: top }]}
          >
            {header}
          </BlurView>
          {underHeader && (
            <View
              style={{
                position: 'absolute',
                zIndex: 1,
                top: headerHeight,
                left: 0,
                right: 0,
              }}
            >
              {underHeader}
            </View>
          )}
        </>
      )}
    </View>
  );
};

export const PageScrollView = (props: PropsScrollView): React.ReactElement => {
  const {
    pageStyle,
    backgroundColor,
    children,
    contentContainerStyle,
    contentPaddingBottom = theme.paddingPageVertical,
    contentPaddingTop = theme.paddingPageVertical,
    header,
    scrollEnabled,
    emptyChild,
    isInModal = true,
    ignoreSafeAreaBottom,
    showFloatingHeader = true,
    onLayout,
  } = props;
  const { top: safeAreaTop } = useSafeAreaInsets();

  const finalShowFloatingHeader = showFloatingHeader && SHOW_FLOATING_HEADER;
  const top = !isInModal ? safeAreaTop : 0;

  const [headerHeight, setHeaderHeight] = React.useState(
    finalShowFloatingHeader ? NAVIGATION_HEADER_HEIGHT + top : 0,
  );
  const handleHeaderLayout = React.useCallback(
    ({
      nativeEvent: {
        layout: { height },
      },
    }: LayoutChangeEvent) => setHeaderHeight(Math.round(height)),
    [],
  );

  const paddingTop = headerHeight;

  return (
    <View style={[{ flex: 1 }, pageStyle]}>
      {!finalShowFloatingHeader && (
        <View style={[styles.headerNotFloating, { paddingTop: top }]}>
          {header}
        </View>
      )}
      {children ? (
        <ScrollView
          onLayout={onLayout}
          scrollEnabled={children ? scrollEnabled : false}
          contentContainerStyle={[contentContainerStyle]}
          backgroundColor={backgroundColor}
          scrollIndicatorInsets={
            headerHeight ? { top: headerHeight - top } : {}
          }
          headerHeight={headerHeight}
          enableSafeAreaBottom={!ignoreSafeAreaBottom}
          contentPaddingBottom={contentPaddingBottom}
          contentPaddingTop={contentPaddingTop}
          keyboardAvoiding
        >
          {children}
        </ScrollView>
      ) : (
        <View
          style={[
            styles.scrollView,
            {
              paddingTop,
              backgroundColor: backgroundColor || theme.colors.background,
            },
          ]}
          onLayout={onLayout}
        >
          {emptyChild}
        </View>
      )}
      {finalShowFloatingHeader && (
        <BlurView
          onLayout={handleHeaderLayout}
          intensity={HEADER_BLUR_INTENSITY}
          tint="light"
          style={[styles.headerFloating, { paddingTop: top }]}
        >
          {header}
        </BlurView>
      )}
    </View>
  );
};
export const Page = (
  props: React.PropsWithChildren<PropsPage>,
): React.ReactElement => {
  const {
    pageStyle,
    backgroundColor,
    children,
    contentPaddingBottom = 0,
    contentPaddingTop = theme.paddingPageVertical,
    header,
    isInModal = true,
    ignoreSafeAreaBottom,
    showFloatingHeader = true,
  } = props;
  const {
    left,
    right,
    top: safeAreaTop,
    bottom: rawSafeAreaBottom,
  } = useSafeAreaInsets();

  const finalShowFloatingHeader = showFloatingHeader && SHOW_FLOATING_HEADER;
  const safeAreaBottom = ignoreSafeAreaBottom ? 0 : rawSafeAreaBottom;
  const top = !isInModal ? safeAreaTop : 0;
  const bottom = !isInModal
    ? safeAreaBottom
    : Platform.OS === 'ios' && !Platform.isPad
      ? safeAreaBottom
      : 0;
  const [headerHeight, setHeaderHeight] = React.useState(
    finalShowFloatingHeader ? NAVIGATION_HEADER_HEIGHT + top : 0,
  );
  const handleHeaderLayout = React.useCallback(
    ({
      nativeEvent: {
        layout: { height },
      },
    }: LayoutChangeEvent) => setHeaderHeight(Math.round(height)),
    [],
  );

  return (
    <View style={[{ flex: 1 }, pageStyle]}>
      {!finalShowFloatingHeader && (
        <View style={[styles.headerNotFloating, { paddingTop: top }]}>
          {header}
        </View>
      )}
      <KeyboardAvoidingView
        style={[
          styles.scrollView,
          {
            paddingBottom: contentPaddingBottom + bottom,
            paddingLeft: left,
            paddingRight: right,
            paddingTop: headerHeight + contentPaddingTop,
            backgroundColor: backgroundColor || theme.colors.background,
          },
        ]}
      >
        {children}
      </KeyboardAvoidingView>
      {finalShowFloatingHeader && (
        <BlurView
          onLayout={handleHeaderLayout}
          intensity={HEADER_BLUR_INTENSITY}
          tint="light"
          style={[styles.headerFloating, { paddingTop: top }]}
        >
          {header}
        </BlurView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  headerFloating: {
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',
    zIndex: 1,
  },
  headerNotFloating: {
    backgroundColor: theme.colors.background,
    borderBottomWidth: 1,
    borderBottomColor: '#ededed',
  },
  scrollView: {
    flex: 1,
  },
});
